import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [hideTimeout, setHideTimeout] = useState(null);

  const handleLoginClick = () => navigate('/login');

  const menuItems = [
    { label: 'Job Seekers', href: '/job-seeker' },
    { label: 'Career Centers', href: '/career-centers' },
    { label: 'Employers', href: '/employer' },
    {
      label: 'Company',
      dropdown: [
        { label: 'About Us', href: '/about-us' },
        { label: 'Features', href: '/features' },
      ],
    },
  ];

  const isCompanyActive = ['/about-us', '/features'].includes(location.pathname);

  const toggleDropdown = (label) => {
    setActiveDropdown((prev) => (prev === label ? null : label));
  };

  useEffect(() => {
    return () => {
      if (hideTimeout) clearTimeout(hideTimeout);
    };
  }, [hideTimeout]);

  return (
    <header className="tw-fixed tw-w-full tw-top-0 tw-z-[1001] tw-bg-white tw-shadow-[0px_4px_16px_#00000014] tw-px-4 sm:tw-px-6 lg:tw-px-14">
      <div className="tw-flex tw-justify-between tw-items-center tw-py-4 lg:tw-h-16">
        {/* Logo */}
        <NavLink to="/" className="tw-flex tw-items-center">
          <img src="images/logo/Logo2.png" alt="logo" className="tw-h-12 tw-w-auto" />
        </NavLink>

        {/* Hamburger Menu */}
        <button
          className="tw-block lg:tw-hidden tw-p-2 tw-bg-transparent tw-border-none"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          {menuOpen ? (
            <svg className="tw-w-6 tw-h-6 tw-text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="tw-w-6 tw-h-6 tw-text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>

        {/* Desktop Navigation */}
        <div className="tw-hidden lg:tw-flex tw-items-center tw-justify-between tw-flex-1 tw-ml-8">
          <nav className="tw-flex tw-items-center tw-gap-8">
            {menuItems.map((item, index) =>
              item.dropdown ? (
                <div
                  key={index}
                  className="tw-relative"
                  onMouseEnter={() => {
                    clearTimeout(hideTimeout);
                    setActiveDropdown(item.label);
                  }}
                  onMouseLeave={() => {
                    const timeout = setTimeout(() => setActiveDropdown(null), 300);
                    setHideTimeout(timeout);
                  }}
                >
                  <span
                    className={`tw-cursor-pointer tw-font-semibold tw-text-base tw-px-3 tw-py-2 tw-rounded-md tw-transition tw-flex tw-items-center tw-gap-1 ${isCompanyActive
                      ? 'tw-active-nav-link-dark tw-shine-effect tw-text-blue-600'
                      : 'tw-text-gray-800 hover:tw-text-blue-600'
                      }`}
                  >
                    {item.label}
                    <svg
                      className={`tw-w-4 tw-h-4 tw-transition-transform ${activeDropdown === item.label ? 'tw-rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </span>

                  {activeDropdown === item.label && (
                    <div className="tw-absolute tw-left-0 tw-top-full tw-z-[10005] tw-bg-white tw-shadow-lg tw-mt-1 tw-rounded-md tw-min-w-[160px] tw-transition-opacity tw-opacity-100 tw-visible">
                      {item.dropdown.map((subItem, subIndex) => (
                        <NavLink
                          key={subIndex}
                          to={subItem.href}
                          className={({ isActive }) =>
                            `tw-block tw-text-sm tw-px-4 tw-py-2 tw-rounded-md tw-transition ${isActive
                              ? 'tw-active-nav-link-dark tw-shine-effect tw-text-blue-600'
                              : 'tw-text-gray-800 hover:tw-text-blue-600'
                            }`
                          }
                        >
                          {subItem.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={index}
                  to={item.href}
                  className={({ isActive }) =>
                    `tw-font-semibold tw-text-base tw-px-4 tw-py-2 tw-rounded-md tw-transition ${isActive
                      ? 'tw-active-nav-link-dark tw-shine-effect tw-text-blue-600'
                      : 'tw-text-gray-800 hover:tw-text-blue-600'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          {/* Desktop Login & Join Buttons */}
          <div className="tw-flex tw-items-center tw-gap-4">
            <span
              onClick={handleLoginClick}
              className="tw-text-gray-800 tw-cursor-pointer tw-font-semibold tw-text-base hover:tw-text-blue-600 tw-transition-colors tw-duration-200"
            >
              Log In
            </span>
            <button
              onClick={() => navigate("/sign-up")}
              className="tw-border-none tw-bg-gradient-to-r tw-from-[#0843FE] tw-to-[#4995FB] hover:tw-opacity-90 tw-text-white tw-font-bold tw-text-base tw-py-2 tw-px-4 tw-rounded-md tw-shadow-lg"
            >
              Join Now
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        className={`tw-bg-white tw-w-full tw-absolute tw-left-0 tw-top-full tw-z-[100] tw-transition-all tw-duration-300 ${menuOpen ? 'tw-max-h-[1000px] tw-opacity-100' : 'tw-max-h-0 tw-opacity-0 tw-overflow-hidden'
          }`}
      >
        <nav className="tw-flex tw-flex-col tw-gap-2 tw-px-4 tw-py-6">
          {menuItems.map((item, index) =>
            item.dropdown ? (
              <div key={index} className="tw-w-full">
                <button
                  onClick={() => toggleDropdown(item.label)}
                  className={`tw-w-full tw-border-none tw-flex tw-justify-between tw-items-center tw-text-left tw-font-semibold tw-text-base tw-py-2 tw-px-3 tw-rounded-md tw-bg-transparent tw-transition ${activeDropdown === item.label || isCompanyActive
                    ? 'tw-active-nav-link-dark tw-shine-effect tw-text-blue-600'
                    : 'tw-text-gray-800 hover:tw-text-blue-600'
                    }`}
                >
                  {item.label}
                  <svg
                    className={`tw-w-4 tw-h-4 tw-ml-1 tw-transition-transform ${activeDropdown === item.label ? 'tw-rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {activeDropdown === item.label && (
                  <div className="tw-pl-6 tw-mt-1 tw-flex tw-flex-col tw-gap-1">
                    {item.dropdown.map((subItem, subIndex) => (
                      <NavLink
                        key={subIndex}
                        to={subItem.href}
                        onClick={() => {
                          setMenuOpen(false);
                          setActiveDropdown(null);
                        }}
                        className={({ isActive }) =>
                          `tw-text-[15px] tw-rounded-md tw-py-1 tw-px-2 tw-transition ${isActive
                            ? 'tw-active-nav-link-dark tw-shine-effect tw-text-blue-600'
                            : 'tw-text-gray-700 hover:tw-text-blue-600'
                          }`
                        }
                      >
                        {subItem.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={index}
                to={item.href}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `tw-font-semibold tw-text-base tw-px-3 tw-py-2 tw-rounded-md ${isActive
                    ? 'tw-active-nav-link-dark tw-shine-effect tw-text-blue-600'
                    : 'tw-text-gray-800 hover:tw-text-blue-600'
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}

          {/* Mobile Login & Join Buttons */}
          <div className="tw-mt-6 tw-border-t tw-border-gray-200 tw-pt-4 tw-flex tw-flex-col tw-gap-3 tw-px-3">
            <button
              className="tw-text-white tw-border-none tw-font-semibold tw-text-base tw-rounded-md tw-bg-blue-600 hover:tw-bg-blue-700 tw-py-2 tw-px-4"
              onClick={handleLoginClick}
            >
              Log In
            </button>
            <button
              onClick={() => {
                setMenuOpen(false);
                navigate("/sign-up");
              }}
              className="tw-border-none tw-bg-gradient-to-r tw-from-[#0843FE] tw-to-[#4995FB] hover:tw-opacity-90 tw-text-white tw-font-bold tw-text-base tw-py-2 tw-px-4 tw-rounded-md tw-shadow-lg"
            >
              Join Now
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
